import React, { FC, useCallback, useContext } from 'react';

import { Box } from '@src/atoms/Box';
import { Unit } from '@src/core/Unit';
import { SwipeDialogContext } from '@src/molecules/SwipeDialog';
import { Text } from '@src/atoms/Text';
import { Link } from '@src/atoms/Link';
import { Divider } from '@src/atoms/Divider';
import { Button } from '@src/atoms/Button';
import { Loader } from '@src/atoms/Loader';
import { ISwipeDialogProps } from '../../models';
import { IComponentChildrenProps } from '@src/models/props';

import styles from './FilterSwipeDialogWrapper.module.scss';
import { Icon } from '@src/atoms/Icon';
import { useChangeHTMLPosition } from '../hooks';
import { FiltersSwipeDialogContext } from '../context';

interface IFilterSwipeDialogWrapperProps
  extends Omit<ISwipeDialogProps, 'controller' | 'onReset' | 'onClose'>,
    IComponentChildrenProps {
  /**
   * Есть ли скролл у внутреннего контейнера, это нужно для того,
   * чтобы отключать функционал свайпера и не блокировать внутренний скролл
   **/
  isScroll?: boolean;

  /**
   * Если в дочернем контейнере есть любое поле открывающее мобильную клавиатуру,
   * нужно передовать `true` для корректной работы верстки в таком случае  */
  isHasInput?: boolean;

  isDisabledSubmitBtn?: boolean;

  title: string;

  onBack?: () => void;

  /** Пропсы для правой кнопки в хедере */
  rightHeaderBtn?: {
    text: string;
    onClick: () => void;
  };
}

/** Общий компонент для отображение разного рода списков */
export const FilterSwipeDialogWrapper: FC<IFilterSwipeDialogWrapperProps> = ({
  rightHeaderBtn,
  onSubmit,
  onBack,
  resultText = 'Применить',
  loading,
  href,
  isScroll,
  isHasInput,
  children,
  title,
  isDisabledSubmitBtn,
}) => {
  const { swipeController } = useContext(SwipeDialogContext);
  const { height } = useContext(FiltersSwipeDialogContext);
  /** Здесь мы отключаем swiper для того, чтобы с помощью touch скроллить */
  const handleTouchStart = useCallback(
    (isScrollContainer?: boolean) => () => isScrollContainer && swipeController?.disable(),
    [swipeController],
  );

  const handleTouchEnd = useCallback(() => swipeController?.enable(), [swipeController]);

  useChangeHTMLPosition(isHasInput);

  return (
    <Unit id="FilterMobileDialog">
      <Box
        fullWidth
        column
        className={styles.root}
        /** Здесь мы получаем полную высоту окна и вычитаем из нее:
         * - отступ от края окна 32px
         * - высоту touch плашки 16px
         * Это свойство очень важно для подсчета динамической высоты согласно контенту внутри
         * НА большинства устройствах работает и так, но не IOS
         *
         **/
        style={{ maxHeight: `calc(${height} - 48px)` }}
      >
        <Box className={styles.header} alignItems="center" justifyContent="center">
          {onBack && (
            <Box padding={8} onClick={onBack} alignItems="center" className={styles.arrowContainer}>
              <Link id={'BackToFilters'}>
                <Icon name="arrow" size="sm" className={styles.arrow} />
              </Link>
            </Box>
          )}

          <Box fullWidth justifyContent="center" alignItems="center" padding={8}>
            <Text id={'Filters-Title'} tag="h4" weight="bold" align="center" className={styles.title}>
              {title}
            </Text>
          </Box>

          {rightHeaderBtn && (
            <Box justifyContent="end" alignItems="center" className={styles.closeContainer}>
              <Link id="cancelLink" onClick={rightHeaderBtn.onClick} noStyles>
                <Text id="cancel" size="primary" className={styles.headerCancelLink}>
                  {rightHeaderBtn.text}
                </Text>
              </Link>
            </Box>
          )}
        </Box>
        <Box>
          <Divider />
        </Box>

        <div onTouchStart={handleTouchStart(isScroll)} onTouchEnd={handleTouchEnd} className={styles.rootWrapper}>
          {children}
        </div>

        <Box paddingLeft={16} paddingRight={16} className={styles.btnContainer}>
          <Button id="SubmitFilter" size="md" disabled={isDisabledSubmitBtn} fullWidth href={href} onClick={onSubmit}>
            {loading ? <Loader view="dark" size="sm" /> : resultText}
          </Button>
        </Box>
      </Box>
    </Unit>
  );
};
